import { useEffect, useState } from 'react';

import * as falso from '@ngneat/falso';
import { CheckboxField } from '@remarkable/ark-web';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useCreateUserRequest } from 'src/api/endpoints/testDataApi';
import type {
  DeviceTypes,
  OrganizationMemberConfig,
  SubscriptionTypes,
} from 'src/api/endpoints/testDataApi.types';
import { Button, Divider, Form, Input, NotificationBox } from 'src/components';

import clsx from 'clsx';
import { useAtom } from 'jotai';
import { CONFIG } from 'src/config';
import { atomWithLocalStorage } from 'src/utils/atomWithLocalStorage';
import { NOT_RM_USER_EMAIL_SUFFIX } from 'src/utils/userHelpers';
import { useGeneratedTestUsers } from '../utils/generatedUsersStore';
import { UserPropertiesDropdown } from './UserPropertiesDropdown';

const deviceDropdownOptions: { value: DeviceTypes; label: string }[] = [
  { value: 'rm2', label: 'reMarkable 2' },
  { value: 'rmpp', label: 'reMarkable Paper Pro' },
  { value: 'rm1', label: 'reMarkable 1' },
];

const subscriptionDropdownOptions: {
  value: SubscriptionTypes;
  label: string;
}[] = [
  { value: 'regular', label: 'Regular' },
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'trial', label: 'Trial' },
];

export const roles = [
  { value: 'rol_dx76SEuCIylXoWAL', label: 'Owner' },
  { value: 'rol_TbVTAO7Tl0vTPvwR', label: 'Admin' },
  { value: 'rol_Dldb25uWbwXafSNl', label: 'Viewer' },
  { value: undefined, label: 'None' }, // Allow no role
];

const storePasswordAtom = atomWithLocalStorage<boolean>(
  'storeTestUserPw',
  false
);

const generateUsername = (options?: { asExternalUser: boolean }) => {
  return `7rdzp.test-service.${falso.randFirstName()}.${falso.randLastName()}${options?.asExternalUser ? NOT_RM_USER_EMAIL_SUFFIX : ''}@inbox.testmail.app`;
};
interface UserForm {
  username: string;
  password: string;
  devices: DeviceTypes[];
  subscription: SubscriptionTypes | null;
  organization: OrganizationMemberConfig | null;
}

export const UserPropertiesForm = () => {
  const createUserMutation = useCreateUserRequest();
  const [showOrganizationFields, setShowOrganizationFields] = useState(false);
  const [asExternalUser, setAsExternalUser] = useState(false);
  const generatedTestUsersStore = useGeneratedTestUsers();
  const [storePasswordPreference, setStorePasswordPreference] =
    useAtom(storePasswordAtom);

  const form = useForm<UserForm>({
    defaultValues: {
      username: generateUsername({ asExternalUser }),
      password: 'dummy-password',
      devices: [],
      subscription: null,
    },
  });

  useEffect(() => {
    form.setValue('username', generateUsername({ asExternalUser }));
  }, [asExternalUser, form]);

  const resetForm = () => {
    form.reset();
    form.setValue('username', generateUsername({ asExternalUser }));
  };

  const onSubmit = (data: UserForm) => {
    createUserMutation.mutate(
      {
        username: data.username,
        password: data.password,
        devices: data.devices,
        subscription: showOrganizationFields ? null : data.subscription,
        organization: showOrganizationFields ? data.organization : null,
      },
      {
        onSuccess: (_, variables) => {
          toast.success('User created successfully');
          generatedTestUsersStore.add({
            username: variables.username,
            password: storePasswordPreference ? variables.password : undefined,
            devices: variables.devices || [],
            subscription: variables.subscription,
            organization: variables.organization,
            actAsExternalUser: asExternalUser,
            createdAt: new Date().toISOString(),
          });
          resetForm();
        },
        onError: (error) => {
          form.setError('root', { message: error.message });
        },
      }
    );
  };

  const isEditable = !createUserMutation.isPending;

  return (
    <>
      <Form
        onSubmit={onSubmit}
        {...form}
        className="flex w-full flex-col gap-16"
      >
        <Input label="Username" name="username" disabled={!isEditable} />
        <Input label="Password" name="password" disabled={!isEditable} />

        <CheckboxField
          className={clsx('mt-16', {
            'pointer-events-none cursor-not-allowed opacity-50': !isEditable,
          })}
          label="Act as external user (frontend UI only)"
          checked={asExternalUser}
          onChange={() => setAsExternalUser(!asExternalUser)}
          disabled={!isEditable}
        />

        <Controller
          name="devices"
          control={form.control}
          render={({ field }) => {
            const selectedDevices = field.value || []; // Ensure it's always an array
            return (
              <UserPropertiesDropdown
                className="w-full"
                label="Select devices (optional)"
                selected={selectedDevices}
                dropdownOptions={deviceDropdownOptions}
                onCheckedChange={(item, checked) =>
                  field.onChange(
                    checked
                      ? [...selectedDevices, item]
                      : selectedDevices.filter((i) => i !== item)
                  )
                }
                disabled={!isEditable}
              />
            );
          }}
        />

        <Controller
          name="subscription"
          control={form.control}
          render={({ field }) => {
            const selectedSubscription = field.value;

            return (
              <UserPropertiesDropdown
                className="w-full"
                label="Select subscription (optional)"
                selected={selectedSubscription ? [selectedSubscription] : null}
                dropdownOptions={subscriptionDropdownOptions}
                onCheckedChange={(item, checked) =>
                  field.onChange(checked ? item : null)
                }
                disabled={!isEditable || showOrganizationFields}
              />
            );
          }}
        />

        <CheckboxField
          name="addToOrganization"
          checked={showOrganizationFields}
          onChange={() => {
            if (!showOrganizationFields && !form.getValues('organization.id')) {
              form.setValue(
                'organization.id',
                CONFIG.DefaultTestOrganizationId
              );
            }
            setShowOrganizationFields(!showOrganizationFields);
          }}
          label="Add to organization (optional)"
          disabled={!isEditable}
          className={
            !isEditable
              ? 'pointer-events-none cursor-not-allowed opacity-50'
              : ''
          }
        ></CheckboxField>

        {showOrganizationFields && (
          <>
            <Input
              name="organization.id"
              label="Organization ID"
              isEditable={isEditable}
              required
            />

            <Controller
              name="organization.role"
              control={form.control}
              render={({ field }) => (
                <UserPropertiesDropdown
                  className="w-full"
                  label="Organization Role"
                  selected={field.value ? [field.value] : null}
                  dropdownOptions={roles}
                  onCheckedChange={(item, checked) =>
                    field.onChange(checked ? item : null)
                  }
                  disabled={!isEditable}
                />
              )}
            />
          </>
        )}

        <Divider />

        <CheckboxField
          label="Store password (at own risk)"
          checked={storePasswordPreference}
          onChange={() => setStorePasswordPreference(!storePasswordPreference)}
        />

        <Button
          data-cy="submit-button"
          type="submit"
          loading={createUserMutation.isPending}
          variant="primary"
          className="w-full"
        >
          Create User
        </Button>
      </Form>

      {createUserMutation.isError && (
        <>
          <NotificationBox
            title="Something went wrong when creating the user"
            error={createUserMutation.error}
          >
            {form.formState.errors.root?.message}
          </NotificationBox>

          <Button
            className="w-full"
            variant="secondary"
            onClick={() => {
              resetForm();
              createUserMutation.reset();
            }}
          >
            Reset form
          </Button>
        </>
      )}
    </>
  );
};
